/* eslint-disable css-modules/no-undef-class */
import classNames from 'classnames';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Typography from '@/components/atoms/typography/typography';
import { isRoomMatrix } from '@/components/modules/configuration-flow/configuration-flow.adapter';
import ConfigurationFlowFooter from '@/components/modules/configuration-flow/configuration-flow-footer/configuration-flow-footer';
import ConfigurationFlowHeader from '@/components/modules/configuration-flow/configuration-flow-header/configuration-flow-header';
import ConfigurationFlowModal from '@/components/modules/configuration-flow/configuration-flow-modal/configuration-flow-modal';
import ConfigurationFlowPaxSelector from '@/components/modules/configuration-flow/configuration-flow-pax-selector/configuration-flow-pax-selector';
import Button from '@/components/molecules/button/button';
import BookingFlowLoader from '@/components/organisms/booking-flow-loader/booking-flow-loader';
import Container from '@/components/utilities/container/container';
import ConfigurationFlowModuleList, {
  ConfigurationFlowModule,
} from '@/components/utilities/content-modules/configuration-flow-module-list/configuration-flow-module-list';
import ModuleWrapper, {
  ModuleWrapperProps,
} from '@/components/utilities/module-wrapper/module-wrapper';
import { useHotelContext } from '@/libraries/contexts/hotel-context/hotel-context';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import { usePredefinedSearchContext } from '@/libraries/contexts/predefined-search-context/predefined-search-context';
import { capitalizeFirstLetter } from '@/libraries/formatters/capitalize-first-letter';
import useIsInView from '@/libraries/hooks/use-is-in-view';
import { usePageTransition } from '@/libraries/hooks/use-page-transition';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import ConfigurationFlowSection from './configuration-flow-section/configuration-flow-section';
// eslint-disable-next-line css-modules/no-unused-class
import styles from './configuration-flow.module.scss';

export interface ConfigurationFlowProps extends ModuleWrapperProps {
  /** The booking url to send the user to */
  bookingUrl?: Content.Components['ConfigurationFlowModuleDto']['bookingUrl'];
  /** The text to show as a headline */
  headline: Content.Components['ConfigurationFlowModuleDto']['headline'];
  /** Parameters from the current page */
  parameters: Content.Components['ConfigurationFlowModuleDto']['parameters'];
  /** Predefined search parameters */
  predefinedSearchParameters?: Content.Components['ConfigurationFlowModuleDto']['predefinedSearchParameters'];
  /** Search settings for the current page */
  searchSettings: Content.Components['ConfigurationFlowModuleDto']['searchSettings'];
  /** The steps of the configuration flow */
  steps: Content.Components['ConfigurationFlowModuleDto']['steps'];
  /** The total price for the offer */
  totalPrice?: Content.Components['ConfigurationFlowModuleDto']['totalPrice'];
}

const INITIAL_STEP = 1;

const ConfigurationFlow = ({
  bookingUrl,
  headline,
  parameters,
  predefinedSearchParameters,
  searchSettings,
  steps,
  totalPrice,
  ...rest
}: ConfigurationFlowProps) => {
  const { t } = useI18n();
  const isDesktop = useWindowSize('desktop');
  const componentRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const {
    bookingUrl: bookingUrlInContext,
    isOpenConfigurationFlow,
    setBookingUrl,
    setCurrentStep,
    setIsOpenConfigurationFlow,
    setIsVisibleConfigurationFlow,
    setParameters,
    setPrice,
    setSearchSettings,
    setStepSummaries,
    totalPrice: priceInTotal,
  } = useHotelContext();
  const { setPredefinedSearchParameters } = usePredefinedSearchContext();
  const [showBookingFlowLoader, setShowBookingFlowLoader] = useState(false);
  const { setIsVisibleInPageConfigurationFlow, setCtaRefPosition } =
    usePageDetailsContext();

  const currentStep = INITIAL_STEP;
  const hasExtraSpacing = isDesktop && isRoomMatrix(steps[0].contentModules[0]);

  useLayoutEffect(() => {
    setBookingUrl(bookingUrl);
    setParameters(parameters);
    setPrice(totalPrice);
    setSearchSettings(searchSettings);
    setStepSummaries(steps.map(step => step.summary));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingUrl, parameters, steps, totalPrice]);

  useLayoutEffect(() => {
    setPredefinedSearchParameters(predefinedSearchParameters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [predefinedSearchParameters]);

  const { isInView: isComponentInView } = useIsInView(componentRef, {
    rootMargin: `0px 0px -50% 0px`,
  });

  const { position: ctaPosition } = useIsInView(ctaRef, {
    rootMargin: `0px 0px 0px 0px`,
  });

  useEffect(() => {
    setCtaRefPosition(ctaPosition);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctaPosition]);

  useEffect(() => {
    setIsVisibleConfigurationFlow(isComponentInView);
    setIsVisibleInPageConfigurationFlow(isComponentInView);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComponentInView]);

  const handleClose = () => {
    setBookingUrl(bookingUrlInContext || bookingUrl);
    setCurrentStep(INITIAL_STEP);
    setIsOpenConfigurationFlow(false);
    setParameters(parameters);
    setStepSummaries(steps.map(step => step.summary));
    setPrice(totalPrice);
    setPredefinedSearchParameters(predefinedSearchParameters);
  };

  usePageTransition({
    onEnter: () => {
      setShowBookingFlowLoader(false);
    },
  });

  // If there are no content modules in the first step, we don't render the component
  // as the rest of the steps are dependent on the first step having content modules
  if (!steps[0]?.contentModules?.length) {
    return null;
  }

  return (
    <ModuleWrapper {...rest} ref={componentRef} className={styles.component}>
      <Container className={styles.container}>
        <ConfigurationFlowHeader
          currentStep={currentStep}
          headline={headline}
          steps={steps}
        />

        <div className={styles.contentWrapper}>
          {steps.map(
            (step, stepIndex) =>
              stepIndex < currentStep && (
                <ConfigurationFlowSection
                  key={stepIndex}
                  currentStep={stepIndex + 1}
                  isActive={stepIndex + 1 === currentStep}
                  isCompleted={stepIndex + 1 < currentStep}
                  step={step}
                />
              ),
          )}

          <div
            className={classNames(styles.content, {
              [styles.hasExtraSpacing]: hasExtraSpacing,
            })}
          >
            {steps[0].headline && (
              <Typography
                className={styles.stepHeadline}
                tagStyle="headingMedium"
              >
                {steps[0].headline}
              </Typography>
            )}

            {searchSettings && (
              <ConfigurationFlowPaxSelector
                className={styles.paxSelector}
                searchSettings={
                  searchSettings as Content.Components['SearchSettingsDto']
                }
              />
            )}

            {steps[0] && (
              <ConfigurationFlowModuleList
                contentModules={
                  steps[0]
                    .contentModules as unknown as ConfigurationFlowModule[]
                }
              />
            )}

            <ConfigurationFlowFooter
              isFirstStep={currentStep === INITIAL_STEP}
              totalPrice={priceInTotal}
            >
              <div ref={ctaRef}>
                {steps.length > 1 ? (
                  <Button
                    onClick={() => {
                      setIsOpenConfigurationFlow(true);
                      setCurrentStep(value => value + 1);
                    }}
                    text={
                      steps[currentStep - 1]?.nextStepText ??
                      capitalizeFirstLetter(t('items.common.words.next'))
                    }
                    variant="primary"
                  />
                ) : (
                  bookingUrl && (
                    <Button
                      onClick={() => setShowBookingFlowLoader(true)}
                      prefetch={false}
                      text={
                        steps[currentStep - 1]?.nextStepText ??
                        t('items.common.words.buyNow')
                      }
                      url={bookingUrlInContext || bookingUrl}
                      variant="primary"
                    />
                  )
                )}
              </div>
            </ConfigurationFlowFooter>
          </div>
        </div>

        <ConfigurationFlowModal
          headline={headline}
          isOpen={isOpenConfigurationFlow}
          onClose={handleClose}
          parameters={parameters}
          setIsOpen={value => setIsOpenConfigurationFlow(value)}
          steps={steps}
        />
      </Container>

      <BookingFlowLoader
        isOpen={showBookingFlowLoader}
        onClose={() => setShowBookingFlowLoader(false)}
      />
    </ModuleWrapper>
  );
};

ConfigurationFlow.displayName = 'ConfigurationFlow';

export default ConfigurationFlow;
